import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { getUsuarioSessao } from '../utils/sessao';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import { abrirDanfeNfePdf } from '../utils/impressaoVenda';
import { hojeBR } from '../utils/data';
import s from './Financeiro.module.css';
import InputDataFiltro from '../components/common/InputDataFiltro';

const fmtBRL = v => parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD   = d => d ? new Date(String(d).slice(0, 10) + 'T12:00').toLocaleDateString('pt-BR') : '—';
const fmtDT  = d => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const hoje   = hojeBR;

export default function Devolucoes() {
  const [aba, setAba]               = useState('nova');
  const [vendaBusca, setVendaBusca] = useState('');
  const [periodoDe, setPeriodoDe]   = useState('');
  const [periodoAte, setPeriodoAte] = useState('');
  const [clienteBusca, setClienteBusca] = useState('');
  const [resultados, setResultados] = useState(null);       // null = sem busca; [] = sem resultados
  const [venda, setVenda]           = useState(null);
  const [qtds, setQtds]             = useState({});          // item_venda_id → quantidade a devolver
  const [dataDev, setDataDev]       = useState(hoje());
  const [motivo, setMotivo]         = useState('');
  const [contaId, setContaId]       = useState('');
  const [contas, setContas]         = useState([]);
  const [historico, setHistorico]   = useState([]);
  const [salvando, setSalvando]     = useState(false);
  const [erro, setErro]             = useState('');
  const [sucesso, setSucesso]       = useState('');
  const [orgData, setOrgData]       = useState(null);
  const [emitindoNfeId, setEmitindoNfeId] = useState(null);
  // Crédito de venda ao consumidor final: exige escolher o cliente do crédito
  const [clienteCredito, setClienteCredito] = useState(null);
  const [buscaCliCred, setBuscaCliCred]     = useState('');
  const [sugCliCred, setSugCliCred]         = useState([]);

  const carregarHistorico = useCallback(async () => {
    try {
      const { data } = await api.get('/devolucoes');
      setHistorico(data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    api.get('/financeiro/contas').then(r => setContas((r.data || []).filter(c => c.ativo))).catch(() => {});
    const u = getUsuarioSessao(); // sessão POR GUIA (cada guia pode estar numa organização)
    if (u?.organizacao_id)
      api.get(`/organizacoes/${u.organizacao_id}`).then(r => setOrgData(r.data)).catch(() => {});
    carregarHistorico();
  }, [carregarHistorico]);

  const abrirVenda = async (id) => {
    setErro(''); setVenda(null); setQtds({});
    try {
      const { data } = await api.get(`/devolucoes/venda/${id}`);
      setVenda(data);
      setResultados(null);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Venda não encontrada.');
    }
  };

  const buscarVenda = async () => {
    setErro(''); setVenda(null); setQtds({}); setResultados(null);
    // Nº da venda informado → abre direto
    if (vendaBusca) return abrirVenda(vendaBusca);
    // Senão, busca por período/cliente
    if (!periodoDe && !periodoAte && !clienteBusca) {
      setErro('Informe o nº da venda, ou um período/cliente para buscar.');
      return;
    }
    try {
      const { data } = await api.get('/vendas', { params: {
        dataInicio: periodoDe  || undefined,
        dataFim:    periodoAte || undefined,
        cliente:    clienteBusca || undefined,
        limite: 50,
      }});
      setResultados((data || []).filter(v => v.status === 'concluida'));
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao buscar vendas.');
    }
  };

  const setQtd = (itemId, v, max) => {
    let q = parseFloat(v);
    if (isNaN(q) || q < 0) q = 0;
    if (q > max) q = max;
    setQtds(p => ({ ...p, [itemId]: q }));
  };

  const itens = venda?.itens || [];
  // Preço líquido = unitário com o desconto da venda rateado (fator_desconto)
  const precoLiq = it => parseFloat(it.preco_liquido ?? it.preco_unitario);
  const temDesconto = venda && venda.fator_desconto && venda.fator_desconto < 0.9999;
  const totalDevolver = itens.reduce((acc, it) =>
    acc + (qtds[it.id] || 0) * precoLiq(it), 0);

  const confirmar = async () => {
    const itensSel = itens
      .filter(it => (qtds[it.id] || 0) > 0)
      .map(it => ({ item_venda_id: it.id, quantidade: qtds[it.id] }));
    if (!itensSel.length) { setErro('Informe a quantidade a devolver de ao menos um item.'); return; }
    const ehCredito = contaId === 'credito';
    const vendaConsumidor = !venda.cliente_id || venda.cliente_id === 1;
    if (ehCredito && vendaConsumidor && !clienteCredito) {
      setErro('Crédito p/ cliente: a venda foi ao consumidor final — escolha o cliente que receberá o crédito.');
      return;
    }
    if (!window.confirm(
      `Confirmar devolução de R$ ${fmtBRL(totalDevolver)} da venda #${venda.id}?\n\n` +
      `• A mercadoria retorna ao estoque\n` +
      `• A dedução vai para 4.3.01 Devoluções de Vendas no DRE (com estorno de CMV)` +
      (ehCredito ? `\n• Gera CRÉDITO P/ CLIENTE, utilizável para baixar crediário/vendas futuras`
        : contaId ? `\n• O reembolso sai da conta financeira selecionada`
        : orgData?.finalizar_venda_pedido
          ? '\n• Sem reembolso financeiro (nenhuma conta selecionada)'
          : '\n• Fica PENDENTE no caixa: reembolso e NF-e são finalizados no PDV (F2)')
    )) return;

    setErro(''); setSalvando(true);
    try {
      await api.post('/devolucoes', {
        venda_id: venda.id,
        data_devolucao: dataDev,
        motivo: motivo || null,
        conta_id: ehCredito || !contaId ? null : parseInt(contaId),
        gera_credito: ehCredito,
        cliente_credito_id: ehCredito ? (clienteCredito?.id || null) : null,
        itens: itensSel,
      });
      setSucesso('Devolução registrada com sucesso!');
      setTimeout(() => setSucesso(''), 4000);
      setVenda(null); setVendaBusca(''); setQtds({}); setMotivo(''); setContaId('');
      setClienteCredito(null); setBuscaCliCred(''); setSugCliCred([]);
      await carregarHistorico();
      setAba('historico');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao registrar devolução.');
    } finally { setSalvando(false); }
  };

  const transmitirNfeDevolucao = async (d) => {
    if (!window.confirm(
      `Transmitir NF-e de DEVOLUÇÃO da devolução #${d.id} (venda #${d.venda_id})?\n\n` +
      `A nota é emitida como ENTRADA (finalidade: devolução), referenciando o documento ` +
      `fiscal da venda original, com CFOP/CST convertidos pelo tipo de movimento "Devolução de Venda".`
    )) return;
    setErro(''); setEmitindoNfeId(d.id);
    try {
      const { data } = await api.post('/fiscal/nfe-devolucao', { devolucao_id: d.id });
      if (data.sucesso) {
        setSucesso(`NF-e de devolução nº ${data.numero} autorizada! Protocolo: ${data.protocolo}`);
        setTimeout(() => setSucesso(''), 6000);
      } else {
        setErro(`NF-e rejeitada: ${data.motivo || data.status}`);
      }
      await carregarHistorico();
    } catch (err) {
      const dta = err.response?.data || {};
      setErro(dta.erro || dta.motivo || 'Erro ao transmitir NF-e de devolução.');
    } finally { setEmitindoNfeId(null); }
  };

  const cancelarNfeDevolucao = async (d) => {
    const just = window.prompt(
      `Cancelar a NF-e de devolução nº ${d.nfe_numero} na SEFAZ?\n\n` +
      `Informe a justificativa (mínimo 15 caracteres):`
    );
    if (just === null) return;
    if (just.trim().length < 15) { setErro('Justificativa deve ter no mínimo 15 caracteres.'); return; }
    const senha = await pedirSenhaAdmin();
    if (senha === null) return;
    setErro(''); setEmitindoNfeId(d.id);
    try {
      const { data } = await api.post(`/fiscal/documentos/${d.nfe_documento_id}/cancelar`,
        { justificativa: just.trim(), senha_admin: senha });
      if (data.sucesso) {
        setSucesso(`NF-e nº ${d.nfe_numero} cancelada na SEFAZ. Protocolo: ${data.protocolo}`);
        setTimeout(() => setSucesso(''), 6000);
      } else {
        setErro(`Cancelamento não homologado: ${data.motivo || ''} (cStat ${data.cStat || '—'})`);
      }
      await carregarHistorico();
    } catch (err) {
      const dta = err.response?.data || {};
      setErro(dta.erro || dta.motivo || 'Erro ao cancelar NF-e.');
    } finally { setEmitindoNfeId(null); }
  };

  const cancelarDevolucao = async (d) => {
    if (!window.confirm(
      `Cancelar a devolução #${d.id} (venda #${d.venda_id})?\n\n` +
      `A mercadoria sai do estoque novamente, os lançamentos no DRE são estornados` +
      (d.conta_id ? ` e o reembolso é removido da conta financeira.` : `.`)
    )) return;
    const senha = await pedirSenhaAdmin();
    if (senha === null) return;
    setErro('');
    try {
      await api.post(`/devolucoes/${d.id}/cancelar`, { senha_admin: senha });
      setSucesso(`Devolução #${d.id} cancelada.`);
      setTimeout(() => setSucesso(''), 4000);
      await carregarHistorico();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao cancelar devolução.');
    }
  };

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>↩</span> Devolução de Vendas</div>

      <div className={s.toolbar}>
        <button className={s.btnErp}
          onClick={() => setAba('nova')}
          title="Iniciar uma nova devolução de venda"
          style={{
            background: aba === 'nova' ? '#15803d' : '#16a34a', color: '#fff',
            fontWeight: 700, fontSize: 14, height: 34, padding: '0 18px',
            border: '1px solid #166534', boxShadow: '0 1px 4px rgba(22,101,52,.35)',
          }}>
          ↩ Nova Devolução
        </button>
        <button className={`${s.btnErp} ${aba === 'historico' ? s.btnErpPrimario : ''}`}
          onClick={() => setAba('historico')}>Histórico</button>
      </div>

      {erro    && <div className={s.erroMsg}>{erro}</div>}
      {sucesso && <div className={s.sucessoMsg}>{sucesso}</div>}

      {aba === 'nova' && (
        <div className={s.conteudo}>
          {/* Busca da venda */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
              Nº da Venda
              <input className={s.inputErp} style={{ width: 100 }} type="number" min="1"
                value={vendaBusca} onChange={e => setVendaBusca(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && buscarVenda()} autoFocus />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
              Período de
              <InputDataFiltro className={s.inputErp} style={{ width: 130 }}
                value={periodoDe} onChange={e => setPeriodoDe(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
              até
              <InputDataFiltro className={s.inputErp} style={{ width: 130 }}
                value={periodoAte} onChange={e => setPeriodoAte(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, minWidth: 180, flex: 1, maxWidth: 280 }}>
              Cliente
              <input className={s.inputErp} placeholder="Nome do cliente..."
                value={clienteBusca} onChange={e => setClienteBusca(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && buscarVenda()} />
            </label>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={buscarVenda}>Buscar</button>
            {venda && (
              <span style={{ fontSize: 12, color: '#555' }}>
                Venda <strong>#{venda.id}</strong> — {venda.cliente_nome || 'Consumidor'} —{' '}
                {fmtDT(venda.criado_em)} — Total <strong>R$ {fmtBRL(venda.total)}</strong>
                <button className={s.btnErp} style={{ marginLeft: 8, height: 20, fontSize: 11, padding: '0 6px' }}
                  onClick={() => { setVenda(null); setQtds({}); }}>✕ trocar venda</button>
              </span>
            )}
          </div>

          {/* Resultados da busca por período/cliente */}
          {!venda && resultados !== null && (
            resultados.length === 0
              ? <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>Nenhuma venda concluída encontrada com esses filtros.</div>
              : (
                <table className={s.tabela} style={{ marginBottom: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>Venda</th>
                      <th style={{ width: 130 }}>Data/Hora</th>
                      <th>Cliente</th>
                      <th style={{ width: 110, textAlign: 'right' }}>Total</th>
                      <th style={{ width: 90 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultados.map(v => (
                      <tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => abrirVenda(v.id)}>
                        <td>#{v.id}</td>
                        <td style={{ fontSize: 11 }}>{fmtDT(v.data)}</td>
                        <td>{v.cliente || 'Consumidor'}</td>
                        <td style={{ textAlign: 'right' }}>R$ {fmtBRL(v.total)}</td>
                        <td>
                          <button className={s.btnErp} style={{ height: 20, fontSize: 11, padding: '0 8px' }}
                            onClick={e => { e.stopPropagation(); abrirVenda(v.id); }}>Selecionar</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          )}

          {venda && (
            <>
              <table className={s.tabela} style={{ marginBottom: 12 }}>
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th style={{ width: 60 }}>UN</th>
                    <th style={{ width: 90, textAlign: 'right' }}>Vendida</th>
                    <th style={{ width: 90, textAlign: 'right' }}>Já devolvida</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Preço Líquido{temDesconto ? ' *' : ''}</th>
                    <th style={{ width: 110 }}>Qtd. a devolver</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map(it => {
                    const max = parseFloat(it.quantidade) - parseFloat(it.qtd_devolvida);
                    const q   = qtds[it.id] || 0;
                    return (
                      <tr key={it.id} style={max <= 0 ? { opacity: 0.5 } : undefined}>
                        <td>{it.descricao}</td>
                        <td>{it.unidade || '—'}</td>
                        <td style={{ textAlign: 'right' }}>{parseFloat(it.quantidade)}</td>
                        <td style={{ textAlign: 'right', color: it.qtd_devolvida > 0 ? '#b45309' : undefined }}>
                          {parseFloat(it.qtd_devolvida)}
                        </td>
                        <td style={{ textAlign: 'right' }} title={temDesconto ? `Preço original R$ ${fmtBRL(it.preco_unitario)} com desconto da venda rateado` : ''}>
                          {fmtBRL(precoLiq(it))}
                        </td>
                        <td>
                          <input className={s.inputErp} type="number" min="0" max={max} step="1"
                            style={{ width: '100%', textAlign: 'right' }}
                            value={q} disabled={max <= 0}
                            onChange={e => setQtd(it.id, e.target.value, max)} />
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: q > 0 ? 700 : 400 }}>
                          {fmtBRL(q * precoLiq(it))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#eef2fa', fontWeight: 700 }}>
                    <td colSpan={6} style={{ textAlign: 'right' }}>Total a devolver:</td>
                    <td style={{ textAlign: 'right', color: '#b91c1c' }}>R$ {fmtBRL(totalDevolver)}</td>
                  </tr>
                </tfoot>
              </table>
              {temDesconto && (
                <div style={{ fontSize: 11, color: '#92400e', margin: '-6px 0 10px' }}>
                  * A venda teve desconto de R$ {fmtBRL(venda.desconto)} — os preços exibidos já estão
                  com o desconto rateado, garantindo estorno igual ao valor efetivamente cobrado.
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
                  Data da Devolução
                  <input type="date" className={s.inputErp} value={dataDev}
                    onChange={e => setDataDev(e.target.value)} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, minWidth: 220 }}>
                  Reembolso
                  <select className={s.selectErp} value={contaId} onChange={e => setContaId(e.target.value)}>
                    {orgData?.finalizar_venda_pedido ? (
                      <>
                        <option value="">— Sem movimentação financeira —</option>
                        <option value="credito">💳 CRÉDITO P/ CLIENTE</option>
                        {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                      </>
                    ) : (
                      <>
                        <option value="">🏪 Finalizar no caixa (PDV — F2)</option>
                        <option value="credito">💳 CRÉDITO P/ CLIENTE</option>
                      </>
                    )}
                  </select>
                </label>
                {contaId === 'credito' && (!venda.cliente_id || venda.cliente_id === 1) && (
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12,
                    minWidth: 260, position: 'relative' }}>
                    Cliente do crédito * <span style={{ color: '#92400e' }}>(venda ao consumidor final)</span>
                    {clienteCredito ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{clienteCredito.nome}</span>
                        <button className={s.btnErp} style={{ height: 22, fontSize: 11 }}
                          onClick={() => { setClienteCredito(null); setBuscaCliCred(''); }}>trocar</button>
                      </div>
                    ) : (
                      <>
                        <input className={s.inputErp} value={buscaCliCred}
                          placeholder="Digite o nome do cliente…"
                          onChange={async e => {
                            const v = e.target.value;
                            setBuscaCliCred(v);
                            if (v.trim().length < 2) { setSugCliCred([]); return; }
                            try {
                              const { data } = await api.get('/clientes', { params: { busca: v, limite: 8 } });
                              setSugCliCred((data || []).filter(c => c.id !== 1).slice(0, 8));
                            } catch { setSugCliCred([]); }
                          }} />
                        {sugCliCred.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
                            background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4,
                            boxShadow: '0 6px 18px rgba(0,0,0,.15)', maxHeight: 200, overflowY: 'auto' }}>
                            {sugCliCred.map(c => (
                              <div key={c.id}
                                style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}
                                onMouseDown={() => { setClienteCredito(c); setSugCliCred([]); }}
                                onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                <strong>{c.nome}</strong>
                                {c.cpf_cnpj && <span style={{ color: '#777' }}> — {c.cpf_cnpj}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </label>
                )}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, flex: 1, minWidth: 240 }}>
                  Motivo
                  <input className={s.inputErp} value={motivo} placeholder="Ex.: defeito, troca, arrependimento..."
                    onChange={e => setMotivo(e.target.value)} />
                </label>
                <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={confirmar}
                  disabled={salvando || totalDevolver <= 0}>
                  {salvando ? 'Registrando...' : `↩ Devolver R$ ${fmtBRL(totalDevolver)}`}
                </button>
              </div>
            </>
          )}

          {!venda && resultados === null && (
            <div style={{ color: '#888', fontSize: 12, padding: '20px 0', textAlign: 'center' }}>
              Busque pelo número da venda, ou por período e/ou cliente, para iniciar a devolução.<br />
              A mercadoria retorna ao estoque e a dedução entra no DRE (4.3.01 — Devoluções de Vendas).
            </div>
          )}
        </div>
      )}

      {aba === 'historico' && (
        <div className={s.conteudo}>
          {historico.length === 0
            ? <div style={{ color: '#888', fontSize: 12 }}>Nenhuma devolução registrada.</div>
            : (
              <table className={s.tabela}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Data</th>
                    <th>Venda</th>
                    <th>Cliente</th>
                    <th style={{ textAlign: 'right' }}>Valor</th>
                    <th>Reembolso</th>
                    <th>Motivo</th>
                    <th>Usuário</th>
                    <th>Status</th>
                    <th>NF-e</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map(d => (
                    <tr key={d.id} style={d.cancelada ? { opacity: 0.55 } : undefined}>
                      <td>{d.id}</td>
                      <td>{fmtD(d.data_devolucao)}</td>
                      <td>#{d.venda_id}</td>
                      <td>{d.cliente_nome || 'Consumidor'}</td>
                      <td style={{ textAlign: 'right', color: '#b91c1c', fontWeight: 600 }}>
                        R$ {fmtBRL(d.total)}
                      </td>
                      <td>
                        {d.credito_valor
                          ? <span style={{ color: '#1e40af' }} title={`Saldo restante: R$ ${fmtBRL(d.credito_saldo)}`}>
                              💳 Crédito p/ cliente
                            </span>
                          : d.conta_nome || '—'}
                      </td>
                      <td style={{ fontSize: 11 }}>{d.motivo || '—'}</td>
                      <td>{d.usuario_nome || '—'}</td>
                      <td>
                        {d.cancelada
                          ? <span className={`${s.badge} ${s.badgeInativo}`}>Cancelada</span>
                          : d.pendente_caixa
                            ? <span className={s.badge} style={{ background: '#fef3c7', color: '#92400e' }}
                                title="Reembolso e NF-e serão finalizados no PDV (F2)">Aguard. caixa</span>
                            : <span className={`${s.badge} ${s.badgeAtivo}`}>Efetivada</span>}
                      </td>
                      <td>
                        {d.nfe_status === 'autorizada' ? (<>
                          <span className={`${s.badge} ${s.badgeAtivo}`}
                            title={`Chave: ${d.nfe_chave || '—'}`}>
                            NF-e nº {d.nfe_numero}
                          </span>
                          <button className={s.btnErp}
                            style={{ height: 20, fontSize: 11, padding: '0 4px', marginLeft: 4 }}
                            title="Imprimir DANFE da NF-e de devolução"
                            onClick={() => abrirDanfeNfePdf(`/fiscal/nfe/${d.nfe_documento_id}/danfe`)}>🖨️</button>
                          <button className={s.btnErp}
                            style={{ height: 20, fontSize: 11, padding: '0 4px', marginLeft: 2, color: '#b91c1c' }}
                            disabled={emitindoNfeId === d.id}
                            title="Cancelar a NF-e de devolução na SEFAZ (evento 110111)"
                            onClick={() => cancelarNfeDevolucao(d)}>✕</button>
                        </>) : (!d.cancelada && !d.pendente_caixa ? (<>
                          {/* NF-e de devolução exige documento fiscal AUTORIZADO na venda de origem */}
                          <button className={s.btnErp}
                            style={{ height: 20, fontSize: 11, padding: '0 6px',
                              color: d.venda_tem_nf ? '#1d4ed8' : '#9ca3af' }}
                            disabled={emitindoNfeId === d.id || !d.venda_tem_nf}
                            title={d.venda_tem_nf
                              ? 'Transmitir NF-e de devolução (entrada, finalidade 4)'
                              : 'Indisponível: a venda de origem não possui NFC-e/NF-e autorizada para referenciar'}
                            onClick={() => transmitirNfeDevolucao(d)}>
                            {emitindoNfeId === d.id ? 'Transmitindo…' : '📤 NF-e'}
                          </button>
                          <button className={s.btnErp}
                            style={{ height: 20, fontSize: 11, padding: '0 6px', marginLeft: 2,
                              color: d.venda_tem_nf ? '#374151' : '#9ca3af' }}
                            disabled={!d.venda_tem_nf}
                            title={d.venda_tem_nf
                              ? 'Prévia do DANFE da devolução (sem valor fiscal)'
                              : 'Indisponível: a venda de origem não possui NFC-e/NF-e autorizada'}
                            onClick={() => abrirDanfeNfePdf('/fiscal/nfe-devolucao/danfe-preview', 'post', { devolucao_id: d.id })}>
                            Prévia
                          </button>
                        </>) : <span style={{ color: '#aaa', fontSize: 11 }}>—</span>)}
                      </td>
                      <td>
                        {!d.cancelada && (
                          <button className={s.btnErp}
                            style={{ height: 20, fontSize: 11, padding: '0 6px', color: '#b91c1c' }}
                            title="Cancelar devolução (estoque e DRE são revertidos)"
                            onClick={() => cancelarDevolucao(d)}>✕ Cancelar</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      )}
    </div>
  );
}
